// The client's replica of the board.
//
// Three parallel typed arrays of NumPixels entries — country, hp, flags — and
// nothing else. 102,400 pixels is 800 KB as Go structs and about 716 KB here;
// 102,400 JavaScript objects is somewhere north of 5 MB and a garbage collection
// pause every time a keyframe lands, which is the difference between a client
// that repaints smoothly and one that stutters for a second every ten.
//
// The replica also tracks which regions changed since the renderer last uploaded,
// because SPEC §3's whole delta path is wasted if the client answers it with a
// full 320x320 texture upload. Dirty tracking is per CHUNK, and per chunk it is a
// bounding box in board coordinates: a tick that touches four pixels on one front
// uploads a handful of texels, and a tick that repaints a chunk uploads a 32x32
// rectangle. Anything finer would cost more in draw-call bookkeeping than it
// saves in bandwidth to the GPU.
//
// `valid` is the gap flag. While it is false the board is not to be drawn or
// clicked against: §3 says a gapped client discards the board, ignores deltas and
// waits for the next board-scope keyframe, and a replica that kept serving stale
// pixels through that window would let a player attack a front that has moved.

import {
  BoardH,
  BoardW,
  ChunkSize,
  ChunksX,
  NumChunks,
  NumPixels,
  chunkOf,
  pixelOf,
  validPixel,
  type ChunkID,
  type PixelID,
} from '../wire/core';
import { deltaPixelID, type Delta } from '../wire/delta';
import { ScopeBoard, type Keyframe } from '../wire/keyframe';

/** A rectangle of board pixels, inclusive of x0/y0 and exclusive of x1/y1. */
export interface DirtyRect {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

export class BoardReplica {
  readonly country = new Uint16Array(NumPixels);
  readonly hp = new Uint32Array(NumPixels);
  readonly flags = new Uint8Array(NumPixels);

  /** False until the first keyframe, and again from a gap until the one that ends it. */
  valid = false;

  /** The tick the pixels are as of. Only meaningful while `valid`. */
  tick = 0;

  // Dirty bookkeeping. A per-chunk bounding box plus a membership list, so
  // consumeDirty is O(dirty chunks) rather than O(NumChunks).
  private readonly rectX0 = new Uint16Array(NumChunks);
  private readonly rectY0 = new Uint16Array(NumChunks);
  private readonly rectX1 = new Uint16Array(NumChunks);
  private readonly rectY1 = new Uint16Array(NumChunks);
  private readonly isDirty = new Uint8Array(NumChunks);
  private dirtyList: ChunkID[] = [];
  private wholeBoardDirty = false;

  /**
   * Applies a KEYFRAME.
   *
   * A board-scope keyframe replaces everything and marks the whole board for
   * re-upload. A chunk-scope keyframe replaces one chunk — it is what brings a
   * newly subscribed chunk up to date on a pan, which is a different job from
   * ending a gap, and it does NOT make the replica valid on its own.
   */
  applyKeyframe(kf: Keyframe): void {
    if (kf.scope === ScopeBoard) {
      this.country.set(kf.country);
      this.hp.set(kf.hp);
      this.flags.set(kf.flags);
      this.valid = true;
      this.tick = kf.tick;
      this.markWholeBoard();
      return;
    }
    const cx = (kf.chunk % ChunksX) * ChunkSize;
    const cy = ((kf.chunk / ChunksX) | 0) * ChunkSize;
    for (let oy = 0; oy < ChunkSize; oy++) {
      const row = (cy + oy) * BoardW + cx;
      const src = oy * ChunkSize;
      for (let ox = 0; ox < ChunkSize; ox++) {
        const p = row + ox;
        const s = src + ox;
        this.country[p] = kf.country[s]!;
        this.hp[p] = kf.hp[s]!;
        this.flags[p] = kf.flags[s]!;
      }
    }
    this.markRect(kf.chunk, cx, cy, cx + ChunkSize, cy + ChunkSize);
    if (this.valid) this.tick = Math.max(this.tick, kf.tick);
  }

  /** Applies a DELTA. Nine bytes a pixel, addressed within its chunk. */
  applyDelta(d: Delta): void {
    for (let i = 0; i < d.count; i++) {
      const p = deltaPixelID(d, i);
      this.country[p] = d.country[i]!;
      this.hp[p] = d.hp[i]!;
      this.flags[p] = d.flags[i]!;
      this.markPixel(p);
    }
    this.tick = d.tick;
  }

  /**
   * Discards the board. §3: on a gap the client throws its pixels away, ignores
   * deltas and waits for the next board-scope keyframe.
   *
   * The arrays are not zeroed. Nothing may read them while `valid` is false, and
   * zeroing 716 KB to prove a point costs a frame at exactly the moment the
   * client is already behind.
   */
  invalidate(): void {
    this.valid = false;
  }

  // --- reads ---------------------------------------------------------------

  countryAt(p: PixelID): number {
    return validPixel(p) ? this.country[p]! : 0;
  }

  hpAt(p: PixelID): number {
    return validPixel(p) ? this.hp[p]! : 0;
  }

  flagsAt(p: PixelID): number {
    return validPixel(p) ? this.flags[p]! : 0;
  }

  /** The pixel under a board coordinate, or -1 off the board. */
  pixelAt(x: number, y: number): PixelID {
    const ix = Math.floor(x);
    const iy = Math.floor(y);
    if (ix < 0 || iy < 0 || ix >= BoardW || iy >= BoardH) return -1;
    return iy * BoardW + ix;
  }

  /** Whether `p` touches a pixel owned by `country`, which is the §5 adjacency rule. */
  isAdjacentTo(p: PixelID, country: number): boolean {
    if (!validPixel(p) || country === 0) return false;
    const x = p % BoardW;
    const y = (p / BoardW) | 0;
    if (x > 0 && this.country[p - 1] === country) return true;
    if (x < BoardW - 1 && this.country[p + 1] === country) return true;
    if (y > 0 && this.country[p - BoardW] === country) return true;
    if (y < BoardH - 1 && this.country[p + BoardW] === country) return true;
    return false;
  }

  // --- dirty tracking ------------------------------------------------------

  private markWholeBoard(): void {
    this.wholeBoardDirty = true;
    this.isDirty.fill(0);
    this.dirtyList.length = 0;
  }

  private markRect(c: ChunkID, x0: number, y0: number, x1: number, y1: number): void {
    if (this.wholeBoardDirty) return;
    if (this.isDirty[c] === 0) {
      this.isDirty[c] = 1;
      this.dirtyList.push(c);
      this.rectX0[c] = x0;
      this.rectY0[c] = y0;
      this.rectX1[c] = x1;
      this.rectY1[c] = y1;
      return;
    }
    if (x0 < this.rectX0[c]!) this.rectX0[c] = x0;
    if (y0 < this.rectY0[c]!) this.rectY0[c] = y0;
    if (x1 > this.rectX1[c]!) this.rectX1[c] = x1;
    if (y1 > this.rectY1[c]!) this.rectY1[c] = y1;
  }

  private markPixel(p: PixelID): void {
    const x = p % BoardW;
    const y = (p / BoardW) | 0;
    this.markRect(chunkOf(p), x, y, x + 1, y + 1);
  }

  /** Whether anything needs uploading. */
  hasDirty(): boolean {
    return this.wholeBoardDirty || this.dirtyList.length > 0;
  }

  /**
   * Takes the dirty regions and clears them. The whole-board case is returned as
   * one rectangle covering the board rather than 100 chunk rectangles, so a
   * keyframe is one texSubImage2D and not a hundred.
   */
  consumeDirty(): DirtyRect[] {
    if (this.wholeBoardDirty) {
      this.wholeBoardDirty = false;
      this.isDirty.fill(0);
      this.dirtyList.length = 0;
      return [{ x0: 0, y0: 0, x1: BoardW, y1: BoardH }];
    }
    const out: DirtyRect[] = [];
    for (const c of this.dirtyList) {
      out.push({ x0: this.rectX0[c]!, y0: this.rectY0[c]!, x1: this.rectX1[c]!, y1: this.rectY1[c]! });
      this.isDirty[c] = 0;
    }
    this.dirtyList = [];
    return out;
  }

  /** Marks a chunk fully dirty, for a caller that changed pixels behind this class's back. */
  markChunkDirty(c: ChunkID): void {
    const x0 = (c % ChunksX) * ChunkSize;
    const y0 = ((c / ChunksX) | 0) * ChunkSize;
    this.markRect(c, x0, y0, x0 + ChunkSize, y0 + ChunkSize);
  }

  /** The global pixel id of a chunk offset, re-exported for callers holding raw deltas. */
  static pixelOf(c: ChunkID, off: number): PixelID {
    return pixelOf(c, off);
  }
}
